<template>
  <b-modal v-model="visible" hide-footer size="xl" dialog-class="modal-fullscreen box-assign-dialog"
           title="📦 入库·箱子分配(입고·박스배정)" @hidden="$emit('close')">
    <!-- 박스 생성 영역 -->
    <div class="border rounded p-2 mb-3 bg-light">
      <div class="d-flex align-items-center flex-wrap">
        <strong class="mr-3">📦 箱子生成(박스 생성)</strong>
        <label class="mb-0 mr-1 small">负责人(담당자)</label>
        <input v-model="담당자이니셜" maxlength="2" placeholder="FK"
               class="form-control form-control-sm mr-2" style="width:70px" @change="saveInitials">
        <div class="btn-group btn-group-sm mr-2">
          <button type="button" :class="['btn', shipmentMode==='auto' ? 'btn-primary' : 'btn-outline-primary']" @click="shipmentMode='auto'">自动(자동)</button>
          <!-- 出库形式 표시 라벨·색(테이프 색) — utils/coupassStatusText. 저장값 milkrun_box/normal_box 는 그대로 -->
          <button type="button" class="btn" :style="outboundTypeStyle('milkrun_box', shipmentMode !== 'milkrun')" @click="shipmentMode='milkrun'">{{ outboundTypeText('milkrun_box') }}</button>
          <button type="button" class="btn" :style="outboundTypeStyle('normal_box', shipmentMode !== 'normal')" @click="shipmentMode='normal'">{{ outboundTypeText('normal_box') }}</button>
        </div>
        <select v-model="boxSize" class="custom-select custom-select-sm mr-2" style="width:150px"
                :class="{ 'border-danger': !boxSize }">
          <option value="">箱子尺寸(박스크기) 선택</option>
          <option v-for="sz in boxSizeOptions" :key="sz" :value="sz">{{ sz }}</option>
        </select>
        <input type="number" v-model.number="boxCount" min="1" class="form-control form-control-sm mr-2" style="width:64px">
        <button type="button" class="btn btn-sm btn-success mr-1" :disabled="!boxSize" @click="addBoxes(boxCount)">+ 箱子 {{ boxCount }}个</button>
        <button type="button" class="btn btn-sm btn-outline-success mr-1" :disabled="!boxSize" @click="addBoxes(1)">+1</button>
        <span v-if="!boxSize" class="small text-danger mr-2">请先选择箱子尺寸(박스크기를 먼저 선택하세요)</span>
        <button type="button" class="btn btn-sm btn-outline-secondary mr-1" @click="loadTodayLabels">🔄</button>
        <button type="button" class="btn btn-sm btn-outline-dark" :disabled="boxes.length === 0" @click="printAllBoxes">🖨 全部标签打印<small>(전체 라벨출력)</small></button>
        <span class="small text-muted ml-2">当前箱子标签(현재 박스 라벨): {{ shipmentLabel }}</span>
      </div>
      <!-- 이미 다른 주문이 든 박스에 이어 담기(2026-10-07 YZY) — 箱号를 스캔하면 그 박스가 아래 카드로 열린다. 같은 화주 박스만 -->
      <div class="d-flex align-items-center flex-wrap mt-2">
        <strong class="mr-2 small">📥 放入已有箱子(기존 박스에 넣기)</strong>
        <!-- 箱号 일부만 쳐도 후보가 뜬다(2026-10-07 YZY 模糊搜索). 스캐너(전체 箱号 + Enter)는 예전처럼 바로 연다 -->
        <div class="position-relative mr-2">
          <input v-model="existingBoxInput" placeholder="扫描/输入箱号(箱号 스캔·일부 입력)" class="form-control form-control-sm" style="width:220px"
                 autocomplete="off" @input="onBoxSearchInput" @keydown.down.prevent="moveBoxPick(1)" @keydown.up.prevent="moveBoxPick(-1)"
                 @keydown.esc="closeBoxSearch" @keyup.enter="onBoxSearchEnter" @blur="onBoxSearchBlur">
          <div v-if="boxSearchOpen" class="border rounded bg-white shadow-sm box-search-menu">
            <div v-if="boxSearchLoading" class="small text-muted px-2 py-1">⏳ 搜索中(검색 중)...</div>
            <div v-else-if="!boxCandidates.length" class="small text-muted px-2 py-1">没有匹配的箱号(일치하는 박스가 없습니다)</div>
            <div v-for="(c, ci) in boxCandidates" :key="'bs-' + c.labelId"
                 class="small px-2 py-1 border-bottom box-search-item"
                 :class="{ 'box-search-item--disabled': c.blocked, 'box-search-item--active': ci === boxPickIndex }"
                 :title="c.blocked"
                 @mousedown.prevent="pickBoxCandidate(c)">
              <div class="d-flex justify-content-between">
                <strong style="white-space:nowrap">{{ c.labelId.replace(/\n/g, '-') }}</strong>
                <span v-if="c.blocked" class="text-danger ml-2" style="white-space:nowrap">{{ c.blocked }}</span>
              </div>
              <div class="text-muted" style="font-size:0.75rem">
                <span v-if="c.companyid !== companyid">{{ c.companyid }} · </span>
                <span v-if="c.orderCount">已装(이미 담김) {{ c.orderCount }}单(주문) / {{ c.qty }}件(개)</span>
                <span v-else>(空/비어있음)</span>
                <span v-if="c.박스크기"> · {{ c.박스크기 }}</span>
              </div>
            </div>
          </div>
        </div>
        <button type="button" class="btn btn-sm btn-outline-primary mr-2" :disabled="!existingBoxInput.trim()" title="先输入箱号才能点击(박스번호를 먼저 입력해야 눌립니다)" @click="openExistingBox">打开(열기)</button>
        <span class="small text-muted">同一货主的不同订单可以装进同一个箱子(같은 화주의 여러 주문을 한 박스에 담을 수 있습니다)<span v-if="!existingBoxInput.trim()" class="d-block text-warning">※ 请先在左侧输入或扫描箱号，「打开」才会亮起(왼쪽 칸에 박스번호를 입력·스캔하면 «열기» 버튼이 활성화됩니다)</span></span>
      </div>
    </div>

    <!-- 박스 카드 영역 -->
    <div class="d-flex flex-wrap mb-3" style="gap:8px">
      <div v-for="box in boxes" :key="box.labelId" class="border rounded p-2" style="width:210px"
           :class="{ 'border-primary': extraBoxIds.includes(box.labelId) }">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <div class="d-flex align-items-center flex-wrap" style="gap:4px">
            <strong class="small" style="white-space:pre-line">{{ box.labelId.replace(/\n/g, '-') }}</strong>
            <!-- 박스크기: 바꾸면 바로 저장. 크기 없이 만든 옛 박스는 빈칸으로 보이고 여기서 채운다 -->
            <select :value="box.박스크기 || ''" class="custom-select custom-select-sm" style="width:auto;height:auto;padding:0 1.5rem 0 4px;font-size:0.75rem"
                    @change="updateBoxSize(box, $event.target.value)">
              <option value="">-</option>
              <option v-for="sz in boxSizeOptions" :key="sz" :value="sz">{{ sz }}</option>
            </select>
          </div>
          <b-dropdown size="sm" variant="link" no-caret toggle-class="p-0 text-dark">
            <template #button-content>⋮</template>
            <b-dropdown-item @click="printBox(box)">🖨 标签打印(라벨출력)</b-dropdown-item>
            <template v-if="otherBoxes(box).length">
              <b-dropdown-divider></b-dropdown-divider>
              <b-dropdown-header>合并此箱到(이 박스를 합치기) →</b-dropdown-header>
              <b-dropdown-item v-for="other in otherBoxes(box)" :key="'mg-' + other.labelId" @click="mergeBox(box, other)">
                {{ other.labelId.replace(/\n/g, '-') }}
              </b-dropdown-item>
            </template>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item variant="danger" @click="emptyBox(box)">清空(비우기)</b-dropdown-item>
            <b-dropdown-item variant="danger" @click="deleteBox(box)">删除箱子(박스 삭제)</b-dropdown-item>
          </b-dropdown>
        </div>
        <!-- 박스 행선지: 출고(출고대기) / 재고(재고대기 → 위치번호 입력 후 중국재고) / 출고확인대기 -->
        <select :value="boxDest[box.labelId] || '출고'" class="custom-select custom-select-sm mb-1"
                :class="{ 'border-warning': boxDest[box.labelId] === '확인', 'border-info': boxDest[box.labelId] === '재고' }"
                @change="$set(boxDest, box.labelId, $event.target.value)">
          <option v-for="d in boxDestOptions" :key="d.value" :value="d.value">{{ d.text }}</option>
        </select>
        <!-- 박스별 发货方式 — 기본은 담은 상품 기준 계산값. 바꾸면 이 박스만 저장(Labels.shipMethodOverride, 2026-10-04 YZY).
             箱号는 그대로 둔다 — 이미 만든 박스는 라벨을 다시 뽑지 않는다(YZY 18:04) -->
        <!-- 선택지 4종(YZY 19:33)·테이프 색(19:36). 옛 저장값 normal_box 는 「红胶带货主地址发货」 로 보인다(boxOutboundKey) -->
        <select :value="addrForm[box.labelId] ? 'customer_address' : boxOutboundKey(box.shipMethodOverride || '')"
                class="custom-select custom-select-sm mb-1 font-weight-bold"
                :style="outboundTypeStyle(boxOutboundKey(box.shipMethodOverride || boxDefaultType(box)), true)"
                :disabled="addrLoading[box.labelId]"
                @change="onShipMethodChange(box, $event.target.value)">
          <option value="" :style="outboundTypeStyle(boxOutboundKey(boxDefaultType(box)), true)">{{ outboundTypeText(boxOutboundKey(boxDefaultType(box))) }} · 自动(자동)</option>
          <option v-for="m in shipMethods" :key="m" :value="m" :style="outboundTypeStyle(m, true)">{{ outboundTypeText(m) }}</option>
        </select>
        <div v-if="addrLoading[box.labelId]" class="small text-muted mb-1">⏳ 正在提取货主地址(화주주소 찾는 중)...</div>
        <div v-if="addrForm[box.labelId]" class="border rounded p-1 mb-1 bg-light">
          <div v-if="addrForm[box.labelId]._source" class="small mb-1" :class="addrForm[box.labelId]._source === 'ai' ? 'text-warning' : 'text-muted'" style="line-height:1.2">
            {{ addrForm[box.labelId]._source === 'ai' ? '⚠ 从其他要求自动提取，请核对(기타요청사항에서 자동 추출 — 확인 후 저장)' : '货主地址(요청내역 화주주소)' }}
          </div>
          <input v-model="addrForm[box.labelId].업체명" placeholder="收件人/公司(업체명)" class="form-control form-control-sm mb-1">
          <input v-model="addrForm[box.labelId].주소" placeholder="地址(주소) *" class="form-control form-control-sm mb-1"
                 :class="{ 'border-danger': !String(addrForm[box.labelId].주소 || '').trim() }">
          <input v-model="addrForm[box.labelId].연락처" placeholder="电话(연락처)" class="form-control form-control-sm mb-1">
          <div class="d-flex justify-content-end" style="gap:4px">
            <button type="button" class="btn btn-sm btn-outline-secondary py-0" @click="$delete(addrForm, box.labelId)">取消(취소)</button>
            <button type="button" class="btn btn-sm btn-primary py-0" :disabled="!String(addrForm[box.labelId].주소 || '').trim()"
                    @click="saveShipMethod(box, 'customer_address', addrForm[box.labelId])">保存(저장)</button>
          </div>
        </div>
        <div v-else-if="box.shipMethodOverride === 'customer_address' && box.customerAddress && box.customerAddress.주소" class="small text-muted mb-1" style="line-height:1.25;word-break:break-all">
          📍 {{ [box.customerAddress.업체명, box.customerAddress.주소, box.customerAddress.연락처].filter(Boolean).join(' / ') }}
          <a href="#" class="ml-1" @click.prevent="editAddress(box)">✎</a>
        </div>
        <!-- 주소를 못 찾아 待客户确认 으로 보낸 박스 — 주소 없이 저장돼 있다. 회신 받으면 ✎ 로 채운다 -->
        <div v-else-if="box.shipMethodOverride === 'customer_address'" class="small text-danger mb-1" style="line-height:1.25">
          ⚠ 地址未填写·待客户确认(주소 미입력 — 고객확인대기)
          <a href="#" class="ml-1" @click.prevent="editAddress(box)">✎</a>
        </div>
        <!-- 이미 담긴 것(앞서 확정한 다른 주문) — 읽기 전용. 이번에 담는 것은 그 아래 입력칸 -->
        <div v-if="boxContentsOf(box.labelId).length" class="small text-muted border-top pt-1">已装(이미 담김) {{ boxContentsOf(box.labelId).length }}</div>
        <div v-for="(c, cidx) in boxContentsOf(box.labelId)" :key="'c-' + cidx"
             class="small d-flex justify-content-between align-items-center py-1 text-muted" style="background:#f6f6f6">
          <span style="max-width:140px;word-break:break-all;line-height:1.25">
            {{ c.상품명 }}
            <span v-if="c.바코드" class="d-block" style="font-size:0.7rem">{{ c.바코드 }}</span>
          </span>
          <span class="text-nowrap pr-1">{{ c.수량 }}</span>
        </div>
        <div v-if="boxItems(box.labelId).length === 0 && boxContentsOf(box.labelId).length === 0" class="text-muted small text-center py-2">(空/비어있음)</div>
        <div v-for="(bi, bidx) in boxItems(box.labelId)" :key="bidx"
             class="small d-flex justify-content-between align-items-center border-top py-1">
          <span style="max-width:110px;word-break:break-all;line-height:1.25">
            {{ bi.상품명 }}
            <span v-if="bi.바코드" class="d-block text-muted" style="font-size:0.7rem">{{ bi.바코드 }}</span>
          </span>
          <span class="d-flex align-items-center">
            <input type="number" v-model.number="bi.수량" min="1"
                   :data-qty-key="bi.boxLabelId + '|' + bi.reqId"
                   class="form-control form-control-sm d-inline-block" style="width:90px" @change="clampBoxQty(bi)">
            <b-dropdown v-if="otherBoxes(box).length" size="sm" variant="link" no-caret toggle-class="p-0 ml-1 text-secondary">
              <template #button-content>⇄</template>
              <b-dropdown-header>移到其他箱子(다른 박스로 이동)</b-dropdown-header>
              <b-dropdown-item v-for="other in otherBoxes(box)" :key="'mv-' + other.labelId" @click="moveAssign(bi, other.labelId)">
                → {{ other.labelId.replace(/\n/g, '-') }}
              </b-dropdown-item>
            </b-dropdown>
            <button type="button" class="btn btn-sm btn-link text-danger p-0 ml-1" @click="removeAssign(bi)">✕</button>
          </span>
        </div>
      </div>
      <div v-if="boxes.length === 0" class="text-muted small p-3">请先生成箱子(박스를 먼저 생성하세요) ↑</div>
    </div>

    <!-- 미배정 상품 풀 -->
    <div class="border rounded p-2">
      <strong class="small">📥 入库商品(입고 상품) — 未分配余量(미배정 잔여)</strong>
      <div class="mt-2 pool-scroll" style="max-height:360px;overflow-y:auto">
      <table class="table table-sm mb-0">
        <thead class="thead-light">
          <tr>
            <th>商品名(상품명)</th><th>SKU</th><th>条码(바코드)</th>
            <th class="text-center">入库(입고)</th><th class="text-center">残量(잔여)</th><th>分配(담기)</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="it in items" :key="it._id">
            <td class="small" style="min-width:160px;word-break:break-all;line-height:1.25">
              {{ it.상품명 }}
              <span v-if="it.옵션1_중국어 || it.옵션2_중국어" class="d-block text-muted" style="font-size:0.75rem">
                {{ it.옵션1_중국어 }}<span v-if="it.옵션2_중국어"> / {{ it.옵션2_중국어 }}</span>
              </span>
              <!-- 清关方式·出库形式 — 다른 쿠패스 화면과 같은 글자·색(utils/coupassStatusText, 2026-10-04 YZY) -->
              <span v-if="customsOf(it) || outboundOf(it)" class="d-block mt-1">
                <span v-if="customsOf(it)" class="badge badge-light border mr-1" style="font-weight:normal">{{ customsOf(it) }}</span>
                <span v-if="outboundOf(it)" class="badge border" style="font-weight:normal" :style="outboundTypeStyle(outboundOf(it))">{{ outboundTypeText(outboundOf(it)) }}</span>
              </span>
            </td>
            <td class="small">{{ it.SKUID }}</td>
            <td class="small">{{ it.바코드 }}</td>
            <td class="text-center">{{ it._입고수량 }}</td>
            <td class="text-center" :class="remaining(it._id) > 0 ? 'text-danger font-weight-bold' : 'text-success'">{{ remaining(it._id) }}</td>
            <td>
              <b-dropdown size="sm" text="箱子에 담기" variant="outline-primary" boundary="window"
                          menu-class="box-pick-menu" :disabled="remaining(it._id) <= 0 || boxes.length === 0">
                <b-dropdown-item v-for="box in boxes" :key="box.labelId" @click="assignToBox(it, box.labelId)">
                  {{ box.labelId.replace(/\n/g, '-') }}
                </b-dropdown-item>
              </b-dropdown>
            </td>
          </tr>
        </tbody>
      </table>
      </div>
      <div class="small text-muted mt-1">※ 未装箱的余量确定时按「待上架库存(재고대기)」处理，在入库列表输入库位号后进入中国库存(박스에 안 담은 잔여는 '재고대기'가 되고, 입고리스트에서 위치번호를 넣으면 '중국재고'가 됩니다).</div>
    </div>

    <div class="text-center mt-3">
      <button type="button" class="btn btn-primary px-4" @click="confirm" :disabled="processing">
        {{ processing ? '处理中(처리중)...' : '入库处理确定(입고처리 확정) → 按箱子去向(박스별 행선지대로)' }}
      </button>
    </div>

    <!-- 라벨 출력 영역 (인쇄용 숨김) -->
    <div id="boxLabelPrintArea" style="display:none">
      <!-- 박스 라벨지 = 가로 80mm × 세로 60mm. 내용을 그 한가운데에 둔다 -->
      <div v-for="lb in printList" :key="'p-' + lb.labelId" style="page-break-after:always;width:100vw;height:100vh;box-sizing:border-box;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;text-align:center;overflow:hidden">
        <!-- 글자는 바코드 자리를 뺀 나머지에 맞춰 크기를 줄인다(긴 업체명이 바코드를 밀어내지 않게) -->
        <div :style="{ fontSize: boxLabelFontSize(lb.labelId) + 'px' }" style="flex:1 1 auto;min-height:0;width:100%;display:flex;align-items:center;justify-content:center;line-height:1.1;font-weight:bold;white-space:pre-line;overflow-wrap:anywhere;overflow:hidden;margin-bottom:2mm">{{ lb.labelId }}</div>
        <!-- 바코드 자리 18mm 고정 -->
        <div style="flex:0 0 18mm;width:100%;line-height:0;text-align:center;display:flex;align-items:center;justify-content:center;gap:2mm"><barcode :value="lb.labelId.replace(/\n/g, '-')" format="CODE128B" :height="60" :width="1" :margin="4" :display-value="false"></barcode><img v-if="boxQr[lb.labelId.replace(/\n/g, '-')]" class="box-qr" :src="boxQr[lb.labelId.replace(/\n/g, '-')]" alt="QR"></div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { customsTypeText, outboundTypeOfReq, outboundTypeInfo, outboundTypeText, outboundTypeStyle, boxOutboundKey, BOX_SHIP_METHODS } from '@/utils/coupassStatusText';
import axios from 'axios';
import qrcode from 'qrcode-generator';

const BOX_LABEL_QR = false;
import VueBarcode from 'vue-barcode';
import { boxLabelFontSize } from '@/utils/boxLabelFit';

// 창고에서 쓰는 박스 크기. 문구는 창고 요청 그대로(Labels.박스크기 에 이 문자열이 저장된다).
const BOX_SIZE_OPTIONS = ['1号大箱(特)', '1号大箱(纸皮)', '1号大箱', '2号箱', '3号小箱'];
// 박스 행선지. value 는 서버 BOX_DEST_STATUS 의 키와 같아야 한다(processWithBox).
const BOX_DEST_OPTIONS = [
  // 표기만 「入库」(2026-10-04 YZY). 저장값 '출고' → 서버 출고대기 는 그대로
  { value: '출고', text: '入库(입고)' },
  { value: '재고', text: '待上架库存(재고)' },
  { value: '확인', text: '待确认出库(출고확인대기)' }
];
// 주소를 못 찾은 「红胶带货主地址发货」 박스 → 그 요청건 需跟客户确认(workMemo)에 붙이는 문구(YZY 19:36)
const ADDRESS_CONFIRM_MEMO = '货主地址发货，请提供收货人、地址、联系电话';

export default {
  name: 'BoxAssignModal',
  components: { barcode: VueBarcode },
  props: {
    show: { type: Boolean, default: false },
    // 입고 대상 (같은 화주, 각 항목에 _입고수량 포함)
    items: { type: Array, default: () => [] },
    companyid: { type: String, default: '' }
  },
  data() {
    return {
      visible: this.show,
      담당자이니셜: '',
      shipmentMode: 'auto', // auto | milkrun | normal
      boxCount: 1,
      boxSize: '',   // 박스 추가 전에 반드시 고른다
      boxSizeOptions: BOX_SIZE_OPTIONS,
      boxDestOptions: BOX_DEST_OPTIONS,
      // 박스별 행선지 { labelId: '출고'|'재고'|'확인' }. 없으면 출고
      boxDest: {},
      // 박스별 发货方式 6종 + 화주 주소 입력 중인 박스 { labelId: { 업체명, 주소, 연락처 } }
      shipMethods: BOX_SHIP_METHODS,
      addrForm: {},
      addrLoading: {},   // { labelId: true } 화주주소 찾는 중
      todayLabels: [],
      // 箱号 스캔으로 연 박스(오늘 것이 아닌 박스 포함) · 박스별 이미 담긴 것 { labelId: [Labels.items + 상태] }
      existingBoxInput: '',
      // 箱号 부분검색 후보 [{ labelId, companyid, orderCount, qty, blocked }] — 같은 화주 먼저
      boxCandidates: [],
      boxSearchOpen: false,
      boxSearchLoading: false,
      boxPickIndex: -1,
      extraBoxes: [],
      boxContents: {},
      // 배정 상태(확정 전 프론트 상태): { reqId, boxLabelId, 상품명, SKUID, 바코드, 수량 }
      assigns: [],
      printList: [],
      boxQr: {}, // 박스번호(-) → QR 그림(2026-10-16 #56)
      processing: false
    };
  },
  computed: {
    boxes() {
      // 오늘 생성된 박스 라벨 중 이 화주 것만. 박스번호 뒷자리 순으로 보여준다 —
      // 창고가 박스를 번호대로 세워놓고 담기 때문에 목록 순서가 그와 같아야 한다.
      // 箱号 스캔으로 연 박스는 오늘 박스 앞에 둔다(지금 이어 담을 박스)
      const today = this.todayLabels
        .filter(l => l.type === 'box' && l.companyid === this.companyid && !this.extraBoxIds.includes(l.labelId))
        .slice()
        .sort((a, b) => (parseInt(a.번호) || 0) - (parseInt(b.번호) || 0));
      return this.extraBoxes.concat(today);
    },
    extraBoxIds() {
      return this.extraBoxes.map(b => b.labelId);
    },
    shipmentLabel() {
      const t = this.resolveShipmentType();
      return t === 'milkrun_box' ? outboundTypeText(t) + ' Z-' : outboundTypeText(t);
    }
  },
  watch: {
    show(v) {
      this.visible = v;
      if (v) this.init();
    }
  },
  methods: {
    outboundTypeText,
    outboundTypeStyle,
    boxOutboundKey,
    customsOf(it) {
      return customsTypeText(it.요청내역 && it.요청내역.customsType);
    },
    outboundOf(it) {
      return outboundTypeOfReq(it);
    },
    init() {
      this.assigns = [];
      this.boxDest = {};
      this.addrForm = {};
      this.addrLoading = {};
      this.existingBoxInput = '';
      this.closeBoxSearch();
      this.extraBoxes = [];
      this.boxContents = {};
      this.loadInitials();
      this.loadTodayLabels();
    },
    loadInitials() {
      // 직원 계정이면 그 계정의 代号(담당자 코드)를 기본값으로 — 브라우저에 남은 값보다 우선. 칸은 그대로 고칠 수 있다.
      // 라벨 이니셜은 2자라서 2자 코드만 채운다. 대표·관리자 계정(코드 없음)은 예전처럼 브라우저 저장값.
      let staffCodeSet = false;
      axios.post('/api/coupass-staff/me', {}).then((res) => {
        const code = res.data && res.data.code;
        if (typeof code === 'string' && /^[A-Z0-9]{2}$/.test(code)) {
          this.담당자이니셜 = code;
          staffCodeSet = true;
        }
      }).catch((e) => { console.error('담당자 코드 조회 실패:', e); });
      try {
        const req = indexedDB.open('CouplusLabels', 1);
        req.onupgradeneeded = (e) => e.target.result.createObjectStore('settings', { keyPath: 'key' });
        req.onsuccess = (e) => {
          const db = e.target.result;
          const tx = db.transaction('settings', 'readonly');
          const get = tx.objectStore('settings').get('담당자이니셜');
          get.onsuccess = () => { if (get.result && !staffCodeSet) this.담당자이니셜 = get.result.value; };
        };
      } catch (e) { console.warn('IndexedDB load failed:', e); }
    },
    saveInitials() {
      try {
        const req = indexedDB.open('CouplusLabels', 1);
        req.onupgradeneeded = (e) => e.target.result.createObjectStore('settings', { keyPath: 'key' });
        req.onsuccess = (e) => {
          const db = e.target.result;
          const tx = db.transaction('settings', 'readwrite');
          tx.objectStore('settings').put({ key: '담당자이니셜', value: this.담당자이니셜 });
        };
      } catch (e) { console.warn('IndexedDB save failed:', e); }
    },
    async loadTodayLabels() {
      const d = new Date();
      const dateStr = String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
      try {
        const res = await axios.post('/api/labels/list', { type: 'box', date: dateStr, companyid: this.companyid });
        this.todayLabels = res.data.labels || [];
        // 오늘 박스 중 이미 담긴 게 있는 것(다른 주문을 먼저 확정한 박스)은 내용물을 같이 보여준다
        const filled = this.todayLabels.filter(l => l.companyid === this.companyid && Array.isArray(l.items) && l.items.length).map(l => l.labelId);
        if (filled.length) await this.loadBoxContents({ labelIds: filled });
      } catch (e) { console.error('loadTodayLabels error:', e); }
    },
    // 박스 내용물(이미 담긴 주문) 조회 → boxContents 에 넣고 박스 목록을 돌려준다
    async loadBoxContents(body) {
      const res = await axios.post('/api/labels/boxContents', body);
      if (res.data.요청결과 !== '성공') throw new Error(res.data.error || 'boxContents 실패');
      (res.data.boxes || []).forEach(b => { this.$set(this.boxContents, b.labelId, b.contents || []); });
      return res.data.boxes || [];
    },
    boxContentsOf(labelId) {
      return this.boxContents[labelId] || [];
    },
    // 箱号 스캔 → 그 박스를 열어 이번 주문을 이어 담는다(2026-10-07 YZY). 같은 화주·아직 창고 안에 있는 박스만
    async openExistingBox() {
      const v = this.existingBoxInput.trim();
      if (!v) return;
      let boxes;
      try {
        boxes = await this.loadBoxContents({ value: v });
      } catch (e) {
        console.error('openExistingBox error:', e);
        return alert('查询箱号失败(박스 조회 실패): ' + e.message);
      }
      const box = boxes[0];
      if (!box) return alert('找不到箱号(박스를 찾을 수 없습니다): ' + v);
      if (box.companyid !== this.companyid) {
        return alert('不同货主的箱子不能混装(다른 화주 박스에는 담을 수 없습니다): ' + box.labelId.replace(/\n/g, '-'));
      }
      if (box.shipped) {
        return alert('此箱已出库，不能再装(이미 출고된 박스라 더 담을 수 없습니다): ' + box.labelId.replace(/\n/g, '-'));
      }
      this.existingBoxInput = '';
      if (!this.extraBoxIds.includes(box.labelId)) this.extraBoxes.unshift(box);
      // 행선지 기본값 = 이미 담긴 것의 상태(재고 박스면 재고, 확인 박스면 확인)
      const st = (box.contents || []).map(c => c.processStatus).find(Boolean) || '';
      if (!this.boxDest[box.labelId]) {
        const dest = (st === '재고대기' || st === '중국재고') ? '재고' : (st === '출고확인대기' ? '확인' : '출고');
        this.$set(this.boxDest, box.labelId, dest);
      }
    },
    // 箱号 부분검색(2026-10-07 YZY) — 입력이 멈추면 후보를 찾는다. 스캐너는 Enter 로 바로 열리므로 여기와 무관
    onBoxSearchInput() {
      clearTimeout(this._boxSearchTimer);
      const q = this.existingBoxInput.trim();
      if (q.length < 2) return this.closeBoxSearch();
      this._boxSearchTimer = setTimeout(() => this.searchBoxes(q), 250);
    },
    async searchBoxes(q) {
      const seq = (this._boxSearchSeq || 0) + 1;
      this._boxSearchSeq = seq;
      this.boxSearchOpen = true;
      this.boxSearchLoading = true;
      this.boxPickIndex = -1;
      try {
        // 같은 화주 것 + 전체(타 화주는 회색 표시용). 같은 화주가 전체 상한에 밀리지 않게 따로 받는다
        const [own, all] = await Promise.all([
          axios.post('/api/labels/list', { type: 'box', q, companyid: this.companyid, limit: 30 }),
          axios.post('/api/labels/list', { type: 'box', q, limit: 20 })
        ]);
        if (seq !== this._boxSearchSeq) return;
        const seen = new Set();
        const labels = (own.data.labels || []).concat(all.data.labels || []).filter(l => {
          if (seen.has(l.labelId)) return false;
          seen.add(l.labelId);
          return true;
        });
        // 담긴 요약·출고 여부는 열기와 같은 boxContents 로 판단한다
        const boxes = labels.length ? await this.loadBoxContents({ labelIds: labels.map(l => l.labelId) }) : [];
        if (seq !== this._boxSearchSeq) return;
        const byId = new Map(boxes.map(b => [b.labelId, b]));
        this.boxCandidates = labels.map(l => {
          const b = byId.get(l.labelId) || l;
          const contents = b.contents || [];
          const blocked = l.companyid !== this.companyid ? '其他货主(다른 화주)' : (b.shipped ? '已出库(출고됨)' : '');
          return {
            labelId: l.labelId,
            companyid: l.companyid,
            박스크기: l.박스크기 || '',
            orderCount: new Set(contents.map(c => String(c.reqId || ''))).size,
            qty: contents.reduce((s, c) => s + (parseInt(c.수량) || 0), 0),
            blocked
          };
        }).sort((a, b) => (a.companyid === this.companyid ? 0 : 1) - (b.companyid === this.companyid ? 0 : 1));
      } catch (e) {
        console.error('searchBoxes error:', e);
        this.boxCandidates = [];
      } finally {
        if (seq === this._boxSearchSeq) this.boxSearchLoading = false;
      }
    },
    closeBoxSearch() {
      clearTimeout(this._boxSearchTimer);
      this._boxSearchSeq = (this._boxSearchSeq || 0) + 1;   // 늦게 온 응답이 다시 열지 않게
      this.boxSearchOpen = false;
      this.boxSearchLoading = false;
      this.boxCandidates = [];
      this.boxPickIndex = -1;
    },
    onBoxSearchBlur() {
      setTimeout(() => { this.boxSearchOpen = false; }, 150);
    },
    moveBoxPick(step) {
      if (!this.boxSearchOpen || !this.boxCandidates.length) return;
      const n = this.boxCandidates.length;
      this.boxPickIndex = (this.boxPickIndex + step + n) % n;
    },
    // Enter: 화살표로 고른 후보가 있으면 그것, 아니면 입력값 그대로(스캐너 = 전체 箱号) 연다
    onBoxSearchEnter() {
      const picked = this.boxSearchOpen && this.boxCandidates[this.boxPickIndex];
      this.closeBoxSearch();
      if (picked) return this.pickBoxCandidate(picked);
      this.openExistingBox();
    },
    pickBoxCandidate(c) {
      if (c.blocked) return;
      this.closeBoxSearch();
      this.existingBoxInput = c.labelId.replace(/\n/g, '-');
      this.openExistingBox();
    },
    // 한 박스에 통관방식·출고형식이 다른 상품을 섞으려 하면 확인을 받는다(화주는 서버·스캔에서 막는다)
    boxMixWarning(it, boxLabelId) {
      const others = this.boxItems(boxLabelId).map(a => this.items.find(i => i._id === a.reqId)).filter(Boolean)
        .concat(this.boxContentsOf(boxLabelId).filter(c => c.요청내역));
      const diffCustoms = others.some(o => customsTypeText(o.요청내역 && o.요청내역.customsType) !== this.customsOf(it));
      const diffOutbound = others.some(o => boxOutboundKey(outboundTypeOfReq(o)) !== boxOutboundKey(this.outboundOf(it)));
      if (!diffCustoms && !diffOutbound) return '';
      return '此箱已有' + (diffCustoms ? '不同清关方式' : '') + (diffCustoms && diffOutbound ? '·' : '') + (diffOutbound ? '不同发货方式' : '') +
        '的商品，确定装在一起吗？(이 박스에 ' + (diffCustoms ? '통관방식' : '') + (diffCustoms && diffOutbound ? '·' : '') + (diffOutbound ? '출고형식' : '') +
        '이 다른 상품이 있습니다. 같이 담을까요?)';
    },
    resolveShipmentType() {
      // 작업자가 토글한 값 우선, auto면 상품의 reqtype/destination으로 판별 (getShipmentType과 동일 규칙)
      if (this.shipmentMode === 'milkrun') return 'milkrun_box';
      if (this.shipmentMode === 'normal') return 'normal_box';
      const it = this.items[0] || {};
      const dest = it.요청내역 && it.요청내역.destination;
      return (it.reqtype === '제트배송' && dest === 'coupang') ? 'milkrun_box' : 'normal_box';
    },
    async addBoxes(count) {
      if (!this.담당자이니셜 || this.담당자이니셜.length !== 2) return alert('请输入负责人缩写2个字符(담당자 이니셜 2자를 입력해주세요)');
      if (!this.companyid) return alert('没有货主信息(화주 정보가 없습니다)');
      if (!this.boxSize) return alert('请先选择箱子尺寸(박스크기를 먼저 선택하세요)');
      try {
        const res = await axios.post('/api/labels/generateBox', {
          companyid: this.companyid,
          담당자이니셜: this.담당자이니셜,
          shipmentType: this.resolveShipmentType(),
          박스크기: this.boxSize,
          count: count || 1
        });
        if (res.data.요청결과 === '성공') {
          await this.loadTodayLabels();
        } else { alert('箱子标签生成失败(박스 라벨 생성 실패)'); }
      } catch (e) { console.error(e); alert('箱子标签生成错误(박스 라벨 생성 오류)'); }
    },
    async updateBoxSize(box, size) {
      const prev = box.박스크기 || '';
      this.$set(box, '박스크기', size);
      try {
        const res = await axios.post('/api/labels/updateBoxSize', { labelId: box.labelId, 박스크기: size });
        if (res.data.요청결과 !== '성공') {
          this.$set(box, '박스크기', prev);
          alert('箱子尺寸保存失败(박스크기 저장 실패): ' + (res.data.error || ''));
        }
      } catch (e) {
        console.error('updateBoxSize error:', e);
        this.$set(box, '박스크기', prev);
        alert('箱子尺寸保存错误(박스크기 저장 오류): ' + e.message);
      }
    },
    // 박스 发货方式 기본값 = 그 박스에 담은 첫 상품의 출고형식(요청 기준). 아직 비었으면 박스 만들 때 정한 형식
    boxDefaultType(box) {
      const a = this.assigns.find(x => x.boxLabelId === box.labelId);
      // 이번에 담은 게 없으면 이미 담긴 것(앞 주문) 기준
      const it = (a && this.items.find(i => i._id === a.reqId)) || this.boxContentsOf(box.labelId).find(c => c.요청내역);
      const t = it ? outboundTypeOfReq(it) : '';
      return (outboundTypeInfo(t) || {}).key || box.shipmentType || '';
    },
    onShipMethodChange(box, v) {
      if (v === 'customer_address') return this.startCustomerAddress(box);
      this.$delete(this.addrForm, box.labelId);
      this.saveShipMethod(box, v, null);
    },
    // 「红胶带货主地址发货」 주소 기본값(YZY 19:36): 이미 저장된 주소 → 요청내역 화주주소 → 기타요청사항에서 AI 추출.
    // 셋 다 없으면 주소 없이 저장하고 이 박스를 待确认出库(고객확인대기)로 돌린다 — 需跟客户确认 문구는 확정(confirm) 때 적는다
    async startCustomerAddress(box) {
      if (box.customerAddress && box.customerAddress.주소) return this.editAddress(box);
      const inBox = [...new Set(this.boxItems(box.labelId).map(a => a.reqId))];
      const reqIds = inBox.length ? inBox : this.items.map(i => i._id);
      this.$set(this.addrLoading, box.labelId, true);
      let res = null;
      try {
        res = (await axios.post('/api/labels/extractShipAddress', { reqIds })).data;
      } catch (e) {
        console.error('extractShipAddress error:', e);
      } finally {
        this.$delete(this.addrLoading, box.labelId);
      }
      if (!res || res.요청결과 !== '성공') {
        if (res) console.error('extractShipAddress fail:', res.error);
        return this.editAddress(box);   // 찾기 실패 — 손으로 입력
      }
      if (res.address && res.address.주소) {
        return this.$set(this.addrForm, box.labelId, Object.assign({ _source: res.source }, res.address));
      }
      const ok = await this.saveShipMethod(box, 'customer_address', { 업체명: '', 주소: '', 연락처: '' }, true);
      if (!ok) return;
      this.$set(this.boxDest, box.labelId, '확인');
      this.$bvToast.toast('未找到货主地址，此箱已改为「待确认出库」，确定入库时自动填写需跟客户确认(화주주소가 없어 고객확인대기로 돌림 — 확정 시 확인내용 자동 작성)', {
        title: '红胶带货主地址发货', variant: 'warning', solid: true
      });
    },
    editAddress(box) {
      const a = box.customerAddress || {};
      this.$set(this.addrForm, box.labelId, { 업체명: a.업체명 || '', 주소: a.주소 || '', 연락처: a.연락처 || '' });
    },
    // 반환: 저장했으면 true
    async saveShipMethod(box, shipMethod, customerAddress, addressPending) {
      try {
        const addr = customerAddress ? { 업체명: customerAddress.업체명, 주소: customerAddress.주소, 연락처: customerAddress.연락처 } : null;
        const res = await axios.post('/api/labels/updateShipMethod', { labelId: box.labelId, shipMethod, customerAddress: addr, addressPending: addressPending === true });
        if (res.data.요청결과 !== '성공') { alert('发货方式保存失败(출고형식 저장 실패): ' + (res.data.error || '')); return false; }
        if (res.data.요청결과 !== '성공') return alert('发货方式保存失败(출고형식 저장 실패): ' + (res.data.error || ''));
        this.$set(box, 'shipMethodOverride', res.data.label.shipMethodOverride);
        this.$set(box, 'customerAddress', res.data.label.customerAddress);
        this.$delete(this.addrForm, box.labelId);
        return true;
      } catch (e) {
        console.error('updateShipMethod error:', e);
        alert('发货方式保存错误(출고형식 저장 오류): ' + e.message);
        return false;
      }
    },
    // 확정 직전 — 주소 없이 「红胶带货主地址发货」 로 둔 박스가 待确认出库 이면 그 요청건 需跟客户确认(workMemo)에 문구를 붙인다.
    // 이미 같은 문구가 있거나 이미 待客户确认(출고확인대기)인 건은 건너뛴다. 실패하면 false(입고하지 않는다)
    async writeAddressConfirmMemos() {
      const pending = this.boxes.filter(b => b.shipMethodOverride === 'customer_address' &&
        !(b.customerAddress && b.customerAddress.주소) && this.boxDest[b.labelId] === '확인');
      const reqIds = [...new Set(pending.reduce((a, b) => a.concat(this.boxItems(b.labelId).map(x => x.reqId)), []))];
      for (const id of reqIds) {
        const it = this.items.find(i => i._id === id);
        if (!it || it.processStatus === '출고확인대기') continue;
        const prev = String((it.요청내역 && it.요청내역.workMemo) || '').trim();
        if (prev.includes(ADDRESS_CONFIRM_MEMO)) continue;
        const value = prev ? prev + '\n' + ADDRESS_CONFIRM_MEMO : ADDRESS_CONFIRM_MEMO;
        try {
          const { data } = await axios.post('/api/reqpurchase/updatereqcontent', { _id: id, field: 'workMemo', value });
          if (!data.success) { alert('需跟客户确认保存失败(확인 내용 저장 실패): ' + (data.message || '')); return false; }
          if (it.요청내역) this.$set(it.요청내역, 'workMemo', value);
        } catch (e) {
          console.error('주소 확인 메모 저장 오류:', e);
          alert('需跟客户确认保存失败(확인 내용 저장 실패): ' + e.message);
          return false;
        }
      }
      return true;
    },
    boxItems(labelId) {
      return this.assigns.filter(a => a.boxLabelId === labelId);
    },
    assignedTotal(reqId) {
      return this.assigns.filter(a => a.reqId === reqId).reduce((s, a) => s + (parseInt(a.수량) || 0), 0);
    },
    remaining(reqId) {
      const it = this.items.find(i => i._id === reqId);
      const inQty = it ? (parseInt(it._입고수량) || 0) : 0;
      return inQty - this.assignedTotal(reqId);
    },
    assignToBox(it, boxLabelId) {
      const rem = this.remaining(it._id);
      if (rem <= 0) return;
      const mix = this.boxMixWarning(it, boxLabelId);
      if (mix && !confirm(mix)) return;
      const existing = this.assigns.find(a => a.reqId === it._id && a.boxLabelId === boxLabelId);
      if (existing) { existing.수량 += rem; } else {
        this.assigns.push({
          reqId: it._id, boxLabelId,
          상품명: it.상품명, SKUID: it.SKUID, 바코드: it.바코드, 수량: rem
        });
      }
      this.focusQty(boxLabelId, it._id);
    },
    removeAssign(bi) {
      const idx = this.assigns.indexOf(bi);
      if (idx >= 0) this.assigns.splice(idx, 1);
    },
    clampBoxQty(bi) {
      const rem = this.remaining(bi.reqId);
      if (rem < 0) bi.수량 += rem;        // 초과분 되돌림
      if (bi.수량 < 1) this.removeAssign(bi);
    },
    emptyBox(box) {
      this.assigns = this.assigns.filter(a => a.boxLabelId !== box.labelId);
    },
    otherBoxes(box) {
      return this.boxes.filter(b => b.labelId !== box.labelId);
    },
    mergeBox(from, to) {
      // from 박스의 상품들을 to 박스로 이동 (같은 상품은 수량 합산)
      const fromItems = this.assigns.filter(a => a.boxLabelId === from.labelId);
      fromItems.forEach(a => {
        const existing = this.assigns.find(x => x !== a && x.reqId === a.reqId && x.boxLabelId === to.labelId);
        if (existing) { existing.수량 += a.수량; this.removeAssign(a); } else { a.boxLabelId = to.labelId; }
      });
    },
    moveAssign(bi, toLabelId) {
      const existing = this.assigns.find(x => x !== bi && x.reqId === bi.reqId && x.boxLabelId === toLabelId);
      if (existing) { existing.수량 += bi.수량; this.removeAssign(bi); } else { bi.boxLabelId = toLabelId; }
    },
    // 박스라벨 = 가로 80mm × 세로 60mm 라벨지 (입고처리 手动打印 과 같은 설정)
    boxLabelPrintOptions() {
      // 바코드는 인쇄 창에서 높이 18mm 로 고정하고, 긴 값은 라벨 폭 92% 안으로 줄인다(벡터라 선명도 그대로)
      const css = '@page{size:80mm 60mm;margin:0}html,body{margin:0;padding:0}svg{height:18mm;width:auto;max-width:92%}img.box-qr{height:18mm;width:18mm}';
      return { name: '_blank', autoClose: true, styles: ['data:text/css;charset=utf-8,' + encodeURIComponent(css)] };
    },
    boxLabelFontSize,
    // 박스 라벨 QR(2026-10-16 杨英文 #56) — 폰 카메라로 찍으면 그 박스의 입고자료 촬영 페이지가 열린다. 서명은 서버가 박스별로 만든다.
    // 서명을 못 받으면 QR 없이 예전처럼 바코드만 인쇄한다(인쇄를 막지 않는다)
    async prepareBoxQr(list) {
      const ids = (list || []).map(b => String(b.labelId || '').replace(/\n/g, '-')).filter(Boolean);
      // 박스 입고자료 QR 은 入库列表 「Coupang入库资料」 창으로 옮겼다(2026-10-18 杨英文 #56 A안) — 라벨엔 바코드만
      if (!BOX_LABEL_QR || !ids.length) { this.boxQr = {}; return; }
      try {
        const { data } = await axios.post('/api/boxPhoto/qr', { labelIds: ids });
        if (data.요청결과 !== '성공') throw new Error((data.error && data.error.errorMessage) || '서명 실패');
        const out = {};
        Object.keys(data.tokens || {}).forEach(id => {
          const qr = qrcode(0, 'M');
          qr.addData(window.location.origin + '/pages/import-photo-box/' + encodeURIComponent(id) + '?t=' + data.tokens[id]);
          qr.make();
          out[id] = qr.createDataURL(4, 0);
        });
        this.boxQr = out;
      } catch (e) {
        console.error('박스 QR 준비 실패 — 바코드만 인쇄:', e);
        this.boxQr = {};
      }
    },
    async printBox(box) {
      this.printList = [box];
      await this.prepareBoxQr(this.printList);
      this.$nextTick(() => { this.$htmlToPaper('boxLabelPrintArea', this.boxLabelPrintOptions()); });
    },
    // 박스를 고르면 잔여 전량이 담긴다. 대개 그중 일부만 담으므로 바로 고쳐 쓸 수 있게
    // 그 칸에 커서를 넣고 값을 선택해 둔다 — 지금은 칸을 다시 찾아 클릭해야 했다.
    focusQty(boxLabelId, reqId) {
      // 박스 라벨에는 개행이 들어 있다(예: Z-woonjung 다음 줄에 FK0922003). 그걸 CSS 속성 셀렉터
      // 문자열에 그대로 끼우면 파싱이 깨져 querySelector 가 예외를 던지고,
      // $nextTick 안이라 Vue 가 조용히 삼킨다 — 포커스가 영영 안 갔다(2026-09-22 실측).
      // 그래서 CSS 파싱을 쓰지 않고 속성값을 직접 비교한다.
      // 드롭다운이 닫히면서 포커스를 토글로 되돌린다. $nextTick 은 그보다 먼저 돌아서
      // 덮어써진다(2026-09-22 실측: activeElement 가 BODY 로 남았다).
      // 그래서 닫힘이 끝난 뒤로 한 박자 미룬다.
      const key = boxLabelId + '|' + reqId;
      const put = () => {
        // document 로 찾는다. b-modal 은 body 로 포털돼서 이 컴포넌트의 $el 은
        // 자리표시 주석(nodeType 8)이고 querySelectorAll 자체가 없다
        // — this.$el 로 찾으면 조용히 죽는다(2026-09-22 실측).
        const els = document.querySelectorAll('[data-qty-key]');
        for (let i = 0; i < els.length; i++) {
          if (els[i].getAttribute('data-qty-key') === key) { els[i].focus(); els[i].select(); return true; }
        }
        return false;
      };
      this.$nextTick(() => { setTimeout(put, 180); });
    },
    // 잘못 만든 박스를 지운다. 담긴 상품이 있으면 서버가 거절하므로 먼저 비우게 한다.
    async deleteBox(box) {
      const name = String(box.labelId).split(String.fromCharCode(10)).join('-');
      if (this.boxItems(box.labelId).length > 0 || this.boxContentsOf(box.labelId).length > 0) {
        return alert('箱内有商品，请先点击「清空」(담긴 상품이 있습니다. 먼저 「비우기」를 눌러주세요)');
      }
      if (!confirm('确定删除箱子 ' + name + ' ?(박스를 삭제하시겠습니까)')) return;
      try {
        const res = await axios.post('/api/labels/deleteBox', { labelId: box.labelId });
        if (res.data.요청결과 === '성공') {
          await this.loadTodayLabels();
        } else {
          alert('删除失败(삭제 실패): ' + (res.data.error || ''));
        }
      } catch (e) {
        console.error('deleteBox error:', e);
        alert('删除错误(삭제 오류): ' + e.message);
      }
    },
    // 오늘 만든 박스 라벨을 번호 순서대로 한 번에 출력한다
    async printAllBoxes() {
      if (!this.boxes.length) return;
      this.printList = this.boxes.slice();
      await this.prepareBoxQr(this.printList);
      this.$nextTick(() => { this.$htmlToPaper('boxLabelPrintArea', this.boxLabelPrintOptions()); });
    },
    async confirm() {
      for (const it of this.items) {
        if (this.remaining(it._id) < 0) return alert(it.상품명 + ' 分配数量超过入库数量(배정수량이 입고수량을 초과했습니다)');
      }
      const assignments = this.items.map(it => {
        const boxes = this.assigns.filter(a => a.reqId === it._id).map(a => ({ boxLabelId: a.boxLabelId, 수량: a.수량, 구분: this.boxDest[a.boxLabelId] || '출고' }));
        return {
          _id: it._id,
          입고수량: parseInt(it._입고수량) || 0,
          boxes,
          중국재고수량: this.remaining(it._id)
        };
      }).filter(a => a.boxes.length || a.중국재고수량 > 0);
      if (!assignments.length) return alert('没有已分配的项目(배정된 항목이 없습니다)');
      this.processing = true;
      if (!(await this.writeAddressConfirmMemos())) { this.processing = false; return; }
      try {
        const res = await axios.post('/api/reqpurchase/import/processWithBox', { assignments });
        if (res.data.요청결과 === '성공') {
          this.$bvToast.toast('入库·箱子分配处理完成(입고·박스배정 처리 완료)', { title: '完成(완료)', variant: 'success', solid: true });
          this.$emit('done', res.data.results);
          this.visible = false;
        } else { alert('处理失败(처리 실패): ' + JSON.stringify(res.data.error)); }
      } catch (e) { console.error(e); alert('处理错误(처리 오류): ' + e.message); } finally { this.processing = false; }
    }
  }
};
</script>

<style>
/* 박스 카드가 많아 두 줄 이상이 되면 아래(미배정 잔여 표)가 잘려 스크롤이 안 되던 것을 푼다(2026-10-16 杨英文 #51).
   원인: 입고처리 화면의 전역 `.modal-fullscreen .modal-body { overflow: hidden }` 가 이 창에도 걸려 있었다.
   다른 전체화면 창(입고처리 작업창 등)의 동작은 그대로 두려고 이 창에만 스크롤을 준다 */
.modal-fullscreen.box-assign-dialog .modal-body {
  overflow-y: auto !important;
}
.pool-scroll thead th { position: sticky; top: 0; z-index: 1; background: #e9ecef; }
/* 「箱子에 담기」 목록이 표 안에서 잘려 앞쪽 박스만 보이던 것을 막는다.
   boundary="window" 로 잘림을 풀고, 박스가 많아지면 목록 안에서 스크롤되게 한다. */
.box-pick-menu {
  max-height: 320px;
  overflow-y: auto;
}
/* 箱号 부분검색 후보 목록 */
.box-search-menu {
  position: absolute;
  top: 100%;
  left: 0;
  z-index: 1050;
  width: 340px;
  max-height: 320px;
  overflow-y: auto;
}
.box-search-item { cursor: pointer; }
.box-search-item:hover, .box-search-item--active { background: #e9f2ff; }
.box-search-item--disabled { cursor: not-allowed; color: #adb5bd; background: #f8f9fa; }
.box-search-item--disabled:hover { background: #f8f9fa; }
</style>
